import * as React from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cn } from '@relay-towing/tokens';
import {
  tabsContentRecipe,
  tabsListRecipe,
  tabsRecipe,
  tabsTriggerRecipe,
  type TabsContentRecipeProps,
  type TabsRecipeProps,
} from '../recipes/tabs.recipe';

type Appearance = 'pill' | 'underline' | 'quiet' | 'heading';

/**
 * Appearance is set once on <Tabs> so the list and every trigger agree. Setting
 * it per-trigger is the kind of thing that produces one odd-looking tab.
 */
const AppearanceContext = React.createContext<Appearance>('pill');

export interface TabsProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root>,
    TabsRecipeProps {
  /**
   * `pill` for switching a view inside a card; `underline` for top-level page
   * sections, where tabs act as navigation rather than as a control;
   * `heading` for tabs that are a phone screen's title, Active / Completed
   * at heading size with an icon before each.
   */
  appearance?: Appearance;
}

/**
 * Tabbed sections.
 *
 * Radix handles roving focus, arrow-key navigation and the tab/panel ARIA
 * relationship. This adds the two appearances and keeps them consistent.
 *
 *   <Tabs defaultValue="details" appearance="underline">
 *     <TabsList>
 *       <TabsTrigger value="details">Details</TabsTrigger>
 *     </TabsList>
 *     <TabsContent value="details">…</TabsContent>
 *   </Tabs>
 *
 * `fill` on the root and on a TabsContent, together, let the open panel take
 * the leftover height of a `Page fill` and scroll inside it.
 */
export const Tabs = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Root>,
  TabsProps
>(function Tabs(
  { appearance = 'pill', fill, className, children, ...props },
  ref
) {
  return (
    <AppearanceContext.Provider value={appearance}>
      <TabsPrimitive.Root
        ref={ref}
        className={cn(tabsRecipe({ fill }), className)}
        {...props}
      >
        {children}
      </TabsPrimitive.Root>
    </AppearanceContext.Provider>
  );
});

export interface TabsListProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> {
  /** Spread the triggers evenly across the full width. */
  fullWidth?: boolean;
}

export const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  TabsListProps
>(function TabsList({ className, fullWidth, ...props }, ref) {
  const appearance = React.useContext(AppearanceContext);
  return (
    <TabsPrimitive.List
      ref={ref}
      className={cn(tabsListRecipe({ appearance, fullWidth }), className)}
      {...props}
    />
  );
});

export interface TabsTriggerProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> {
  fullWidth?: boolean;
}

export const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  TabsTriggerProps
>(function TabsTrigger({ className, fullWidth, ...props }, ref) {
  const appearance = React.useContext(AppearanceContext);
  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(tabsTriggerRecipe({ appearance, fullWidth }), className)}
      {...props}
    />
  );
});

export interface TabsContentProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>,
    TabsContentRecipeProps {}

export const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  TabsContentProps
>(function TabsContent({ className, gap, fill, ...props }, ref) {
  return (
    <TabsPrimitive.Content
      ref={ref}
      className={cn(tabsContentRecipe({ gap, fill }), className)}
      {...props}
    />
  );
});
